/**
 * ============================================================================
 * useExportKpiMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/suppliers/kpi-dashboard/export — the server returns
 *          a signed download URL (and emits a DASHBOARD_EXPORTED audit row
 *          per QMD-EXFIL-001). We download from there.
 *
 * Why server-side: the table lives in the API; the user's filter context
 *  is also there. Doing the CSV on the client would (a) miss the audit row
 *  and (b) drift from the canonical column order on the next phase upgrade.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { KpiDashboardParams } from './useKpiDashboardQuery';

export interface ExportKpiResponse {
  /** The CSV content, returned inline so the SPA can blob-download it with the
   *  Bearer token attached (a new-tab download can't carry the token). F0-DL. */
  csv: string;
  /** Server-suggested filename. */
  filename: string;
  /** Number of supplier rows in the export. */
  row_count: number;
  /** Audit envelope id for the DASHBOARD_EXPORTED event. */
  audit_event_id: string;
}

export function useExportKpiMutation(): UseMutationResult<
  ExportKpiResponse,
  Error,
  KpiDashboardParams
> {
  return useMutation<ExportKpiResponse, Error, KpiDashboardParams>({
    mutationFn: (params) =>
      apiFetch<ExportKpiResponse>(
        '/api/v1/suppliers/kpi-dashboard/export',
        {
          method: 'POST',
          body: params,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
